/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { FunctionComponent } from "react";

import {
    StrapiArticleBodyDynamicZone,
    StrapiTerminusSidebarAd,
    StrapiTerminusTopic,
} from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { Text } from "@/ui/atoms/text";
import { TerminusSidebarAd } from "@/ui/molecules/terminus_sidebar_ad";
import { TerminusSidebarAnchors } from "@/ui/molecules/terminus_sidebar_anchors";
import { TerminusSidebarTopics } from "@/ui/molecules/terminus_sidebar_topics";

import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";

import { Divider } from "@/ui/atoms/divider";

export interface TerminusSidebarProps {
    ad?: StrapiTerminusSidebarAd;
    article?: StrapiArticleBodyDynamicZone;
    className?: SerializedStyles;
    topics?: StrapiTerminusTopic[];
}

export const TerminusSidebar: FunctionComponent<TerminusSidebarProps> = (
    props,
) => {
    const { ad, article, topics } = props;

    /**
     * Styles
     */
    const theme = useTypedTheme();

    const sidebarWrapperStyles = css(
        {
            alignContent: "start", // only end up applying on the extraSmall breakpoint
            flexDirection: "column", // only end up applying on the extraSmall breakpoint
            height: "100%", // to give container room to position sticky
        },
        buildStylesByBreakpoint("display", {
            extraSmall: "flex", // enabled to switch the order of children on small so the ad is on top
            medium: "block",
        }),
    );

    const sidebarStyles = css({
        backgroundColor: Colors[theme.backgrounds.backgroundTertiary],
        borderRadius: BorderRadiuses.borderSmall,
        padding: Spacing["spacing-2"],
    });

    const sidebarHeadlineStyles = css({
        backgroundColor: Colors[theme.backgrounds.backgroundTertiary],
        borderRadius: BorderRadiuses.borderSmall,
        display: "block",
        marginBottom: Spacing["spacing-3"],
        padding: Spacing["spacing-2"],
    });

    return (
        <div css={sidebarWrapperStyles}>
            {topics?.length && (
                <GridColumn
                    className={css(
                        sidebarStyles,
                        {
                            position: !ad ? "sticky" : "static",
                            top: Spacing["spacing-24"],
                        },
                        buildStylesByBreakpoint("order", {
                            extraSmall: ad && topics && 1,
                            medium: 0,
                        }),
                        buildStylesByBreakpoint("marginBottom", {
                            extraSmall: Spacing["spacing-12"],
                            medium: Spacing["spacing-4"],
                        }),
                        buildStylesByBreakpoint("marginTop", {
                            extraSmall: Spacing["spacing-12"],
                            medium: 0,
                        }),
                    )}
                >
                    <Text
                        isUppercase
                        className={sidebarHeadlineStyles}
                        fontFamily="mono"
                        fontSize="TEXT_075"
                    >
                        Terminus by Warp
                    </Text>

                    {article && (
                        <>
                            <TerminusSidebarAnchors article={article} />

                            <Divider
                                marginBottom="TerminusDividerMargin"
                                marginTop="TerminusDividerMargin"
                            />
                        </>
                    )}

                    <TerminusSidebarTopics
                        isCollapsible={article ? true : false}
                        topics={topics}
                    />
                </GridColumn>
            )}

            {ad && (
                <GridColumn
                    className={css(
                        sidebarStyles,
                        {
                            top: Spacing["spacing-24"],
                        },
                        buildStylesByBreakpoint("position", {
                            medium: "sticky",
                        }),
                        buildStylesByBreakpoint("marginTop", {
                            extraSmall: Spacing["spacing-8"],
                            medium: 0,
                        }),
                    )}
                >
                    <TerminusSidebarAd ad={ad} />
                </GridColumn>
            )}
        </div>
    );
};
